import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, SlidersHorizontal, Phone, Video, Wifi, X } from 'lucide-react';

const FILTER_PILLS = [
  { key: 'online', label: 'Online Now', icon: Wifi, filter: 'availability', value: 'online' },
  { key: 'audio', label: 'Audio', icon: Phone, filter: 'callType', value: 'audio' },
  { key: 'video', label: 'Video', icon: Video, filter: 'callType', value: 'video' },
  { key: 'low', label: 'Under $2/min', filter: 'priceRange', value: 'low' },
  { key: 'mid', label: '$2–$5/min', filter: 'priceRange', value: 'mid' },
  { key: 'high', label: '$5+/min', filter: 'priceRange', value: 'high' },
];

export default function SearchFilterBar({ searchText, setSearchText, filters, setFilters, expertiseOptions, countryOptions = [], cityOptions = [] }) {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const debounceRef = useRef(null);
  const [localSearch, setLocalSearch] = useState(searchText);

  // Debounced search
  const handleSearchChange = useCallback((value) => {
    setLocalSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchText(value);
    }, 300);
  }, [setSearchText]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const togglePill = (filterKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: prev[filterKey] === value ? 'all' : value,
    }));
  };

  const activeFilterCount = Object.values(filters).filter((v) => v !== 'all').length;
  const hasActiveFilters = activeFilterCount > 0 || searchText.trim() !== '';

  const clearAll = () => {
    setLocalSearch('');
    setSearchText('');
    setFilters({
      availability: 'all',
      callType: 'all',
      priceRange: 'all',
      expertise: 'all',
      country: 'all',
      city: 'all',
    });
  };

  return (
    <div className="sticky top-0 z-30 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 pt-3 pb-2">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={localSearch}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('discover.searchPlaceholder', 'Search by name, expertise, industry or language…')}
            className="w-full pl-10 pr-20 h-11 rounded-xl border border-gray-300 dark:border-gray-700
              bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100
              placeholder-gray-400 dark:placeholder-gray-500
              focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent
              text-sm transition-all"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {hasActiveFilters && (
              <button
                onClick={clearAll}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                title={t('discover.clearAll', 'Clear all')}
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`p-1.5 rounded-lg transition-colors ${
                showAdvanced || activeFilterCount > 0
                  ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={t('discover.filters', 'Filters')}
            >
              <SlidersHorizontal className="w-4 h-4" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Search hint — only when empty, so it guides without cluttering */}
        {!localSearch && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500 px-1 mt-0.5">
            {t('discover.searchTip', 'Tip: search a name, an expertise like “life coach”, or a language like “Spanish”.')}
          </p>
        )}

        {/* Quick Filter Pills */}
        <div
          className="flex gap-2 py-2.5 overflow-x-auto"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <style jsx>{`
            div::-webkit-scrollbar { display: none; }
          `}</style>
          {FILTER_PILLS.map((pill) => {
            const isActive = filters[pill.filter] === pill.value;
            const Icon = pill.icon;
            return (
              <button
                key={pill.key}
                onClick={() => togglePill(pill.filter, pill.value)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium
                  transition-all duration-200 border
                  ${isActive
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-transparent shadow-md'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-500'
                  }`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {t(`discover.pill_${pill.key}`, pill.label)}
              </button>
            );
          })}
        </div>

        {/* Advanced Filters Panel */}
        {showAdvanced && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pb-3 animate-in slide-in-from-top-2">
            <select
              value={filters.expertise}
              onChange={(e) => setFilters((prev) => ({ ...prev, expertise: e.target.value }))}
              className="h-9 px-3 rounded-lg border border-gray-300 dark:border-gray-700
                bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">{t('discover.allExpertise', 'All Expertise')}</option>
              {expertiseOptions?.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>

            <select
              value={filters.availability}
              onChange={(e) => setFilters((prev) => ({ ...prev, availability: e.target.value }))}
              className="h-9 px-3 rounded-lg border border-gray-300 dark:border-gray-700
                bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">{t('discover.allStatus', 'All Status')}</option>
              <option value="online">{t('discover.statusOnline', 'Online')}</option>
              <option value="busy">{t('discover.statusBusy', 'Busy')}</option>
              <option value="offline">{t('discover.statusOffline', 'Offline')}</option>
            </select>

            <select
              value={filters.callType}
              onChange={(e) => setFilters((prev) => ({ ...prev, callType: e.target.value }))}
              className="h-9 px-3 rounded-lg border border-gray-300 dark:border-gray-700
                bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">{t('discover.allCallTypes', 'All Call Types')}</option>
              <option value="audio">{t('discover.audioOnly', 'Audio Only')}</option>
              <option value="video">{t('discover.videoOnly', 'Video Only')}</option>
            </select>

            <select
              value={filters.priceRange}
              onChange={(e) => setFilters((prev) => ({ ...prev, priceRange: e.target.value }))}
              className="h-9 px-3 rounded-lg border border-gray-300 dark:border-gray-700
                bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">{t('discover.allPrices', 'All Prices')}</option>
              <option value="low">{t('discover.priceLow', 'Under $2/min')}</option>
              <option value="mid">{t('discover.priceMid', '$2 – $5/min')}</option>
              <option value="high">{t('discover.priceHigh', '$5+/min')}</option>
            </select>

            {/* Country — picking one resets City so it stays scoped to the country */}
            <select
              value={filters.country}
              onChange={(e) => setFilters((prev) => ({ ...prev, country: e.target.value, city: 'all' }))}
              className="h-9 px-3 rounded-lg border border-gray-300 dark:border-gray-700
                bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">{t('discover.allCountries', 'All Countries')}</option>
              {countryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            {/* City — options are scoped to the selected country */}
            <select
              value={filters.city}
              onChange={(e) => setFilters((prev) => ({ ...prev, city: e.target.value }))}
              disabled={cityOptions.length === 0}
              className="h-9 px-3 rounded-lg border border-gray-300 dark:border-gray-700
                bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              <option value="all">{t('discover.allCities', 'All Cities')}</option>
              {cityOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
